<template>
  <div>
    <v-dialog v-model="dialog" persistent width="840" scrollable>
      <v-card>
        <v-card-title class="text-wrap">Licence Expérience FFTRI</v-card-title>
        <v-card-text>
          <div>Vous n'êtes pas licencié FFTRI. Afin de participer à cette compétition, vous devez faire l'acquisition
            d'une licence Expérience FFTRI. Le tarif de la licence est calculé en fonction du parcours sélectionné. Vous
            devrez également compléter le formulaire info santé ci-dessous.
          </div>

          <div v-if="loading_price" class="text-center py-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else>
            <v-switch v-model="hasWtForeignLicense" color="primary" hide-details density="compact" class="mt-3"
              label="Je suis titulaire d'une licence d'une fédération de triathlon étrangère affiliée à World Triathlon"></v-switch>

            <div v-if="hasWtForeignLicense" class="mt-3">
              <div v-if="loading_federations" class="text-center py-2">
                <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
              </div>
              <template v-else>
                <v-autocomplete v-model="id_federation" :items="federation_items" item-title="label" item-value="id"
                  label="Fédération étrangère" density="compact" clearable @update:model-value="on_federation_change">
                </v-autocomplete>
                <v-text-field v-model="numero_licence_etrangere" label="Numéro de votre licence étrangère"
                  density="compact" :rules="[rules.min_licence_num]" />
                <v-alert v-if="wt_price_message" type="info" variant="tonal" density="compact" class="mt-2">
                  {{ wt_price_message }}
                </v-alert>
              </template>
            </div>

            <p class="font-weight-bold my-2">Prix de la licence : {{ formatted_price }}</p>
            <v-switch v-model="receivePromo" color="primary" hide-details density="compact"
              label="J'accepte de recevoir des informations promotionnelles de la FFTRI par email"></v-switch>
            <v-switch v-model="sendInvoice" color="primary" hide-details density="compact" class="mb-4"
              label="Je souhaite recevoir une facture pour cette licence"></v-switch>
            <v-divider class="my-3"></v-divider>
            <FormulaireInfoSanteFFTRI v-model="health_form" />
          </div>
        </v-card-text>
        <v-card-actions class="flex-column flex-sm-row gap pa-4">
          <v-btn @click="$emit('unvalidate')" color="dark-red" class="sm-fullwidth">Annuler</v-btn>
          <v-btn @click="confirm" color="dark-green" class="sm-fullwidth" :disabled="!can_confirm || loading_price">
            Confirmer et ajouter au panier
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import PublicService from "@/services/public.service";
import FormulaireInfoSanteFFTRI from "./FormulaireInfoSanteFFTRI.vue";
import {
  createEmptyHealthFormFFTRI,
  isHealthFormFFTRIComplete,
} from "./formulaire_info_sante_fftri.js";

export default {
  components: { FormulaireInfoSanteFFTRI },
  props: ["inscription"],
  data() {
    return {
      dialog: true,
      loading_price: true,
      loading_federations: false,
      prix: 0,
      base_prix: 0,
      currency: "EUR",
      receivePromo: false,
      sendInvoice: true,
      id_course: null,
      type_licence_experience: "",
      health_form: createEmptyHealthFormFFTRI(),
      hasWtForeignLicense: false,
      federations: [],
      id_federation: null,
      numero_licence_etrangere: "",
      wt_free: false,
      rules: {
        min_licence_num: (v) => (v && String(v).trim().length >= 2) || "Numéro de licence requis (min. 2 caractères)",
      },
    };
  },
  computed: {
    federation_items() {
      return this.federations.map((f) => ({
        id: parseInt(f.id, 10),
        label: `${f.name_country || ""} — ${f.name_federation || ""}`.trim(),
        WTFree: f.WTFree,
      }));
    },
    formatted_price() {
      if (this.prix == null || isNaN(this.prix)) return "—";
      if (this.prix === 0 && this.hasWtForeignLicense && this.id_federation) {
        return "Gratuit";
      }
      return new Intl.NumberFormat("fr-FR", { style: "currency", currency: this.currency || "EUR" }).format(this.prix);
    },
    wt_price_message() {
      if (!this.id_federation) {
        return "Sélectionnez votre fédération pour connaître le tarif du Pass Compétition.";
      }
      if (this.wt_free) {
        return "Votre fédération bénéficie d'une gratuité de Pass Compétition World Triathlon : la licence Expérience est gratuite.";
      }
      return "Votre fédération ne bénéficie pas de la gratuité : vous devez prendre un Pass Compétition classique au tarif indiqué.";
    },
    can_confirm() {
      if (!isHealthFormFFTRIComplete(this.health_form)) {
        return false;
      }
      if (this.hasWtForeignLicense) {
        const num = (this.numero_licence_etrangere || "").trim();
        if (!this.id_federation || num.length < 2) {
          return false;
        }
        return this.prix === 0 ? this.wt_free : this.prix > 0;
      }
      return this.prix > 0;
    },
  },
  watch: {
    hasWtForeignLicense(val) {
      if (val) {
        this.load_federations();
        this.on_federation_change(this.id_federation);
      } else {
        this.id_federation = null;
        this.numero_licence_etrangere = "";
        this.wt_free = false;
        this.prix = this.base_prix;
      }
    },
  },
  methods: {
    get_selected_course() {
      const selected = this.inscription.list_courses.list.filter((c) => c.selected);
      return selected.length > 0 ? selected[0] : null;
    },
    close_without_price() {
      this.dialog = false;
      this.$emit("unvalidate");
    },
    async load_federations() {
      if (this.federations.length > 0) {
        return;
      }
      this.loading_federations = true;
      const res = await PublicService.getAllWtFederations();
      if (res && res.found && Array.isArray(res.federations)) {
        this.federations = res.federations;
      } else {
        this.federations = [];
      }
      this.loading_federations = false;
    },
    async on_federation_change(federationId) {
      if (!this.hasWtForeignLicense || !federationId) {
        this.wt_free = false;
        this.prix = this.base_prix;
        return;
      }
      const res = await PublicService.getLicenceExperiencePrice(
        this.id_course,
        this.type_licence_experience,
        federationId
      );
      const prix = res && res.prix != null ? parseFloat(res.prix) : NaN;
      if (!res || !res.found || isNaN(prix)) {
        this.wt_free = false;
        this.prix = this.base_prix;
        return;
      }
      this.wt_free = res.wt_free === true;
      this.prix = prix;
      this.currency = res.currency || this.currency;
    },
    async fetch_price() {
      this.loading_price = true;
      const course = this.get_selected_course();
      if (!course) {
        this.loading_price = false;
        this.close_without_price();
        return;
      }
      this.id_course = course.id;
      this.type_licence_experience = course.metadatas?.type_licence_experience || "";
      if (!this.type_licence_experience) {
        this.loading_price = false;
        this.close_without_price();
        return;
      }
      const res = await PublicService.getLicenceExperiencePrice(this.id_course, this.type_licence_experience);
      const prix = res && res.prix != null ? parseFloat(res.prix) : NaN;
      if (!res || !res.found || isNaN(prix) || prix <= 0) {
        this.loading_price = false;
        this.close_without_price();
        return;
      }
      this.base_prix = prix;
      this.prix = prix;
      this.currency = res.currency || "EUR";
      this.loading_price = false;
    },
    confirm() {
      if (!this.can_confirm) return;
      const payload = {
        prix: this.prix,
        receivePromo: this.receivePromo ? 1 : 0,
        sendInvoice: this.sendInvoice ? 1 : 0,
        health_form: { ...this.health_form },
        id_course: this.id_course,
        type_licence_experience: this.type_licence_experience,
        confirmed: true,
        id_federation: null,
        numero_licence_etrangere: "",
      };
      if (this.hasWtForeignLicense) {
        payload.id_federation = this.id_federation;
        payload.numero_licence_etrangere = (this.numero_licence_etrangere || "").trim();
      }
      this.$emit("validate", payload);
    },
  },
  created() {
    this.fetch_price();
  },
};
</script>
